import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../../common/components/functional/dialogs/BasicDialog";
import { useAppSelector } from "../../../app/hooks";
import useIsLoading from "../../../hooks/useIsLoading";
import { TalentBlacklist } from "../../talents/talentAPI";
import { AddBlacklistForm } from "./AddBlacklistForm";
import {
  BlacklistFormValues,
  defaultBlacklistFormValues,
  mapEntryToBlacklistFormValues,
} from "./blacklistMappers";

type Props = {
  isOpen: boolean;
  editingEntry?: TalentBlacklist | null;
  saveBlacklist: (formData: BlacklistFormValues) => void;
  closeDialog: () => void;
  isLoading?: boolean;
};

export const AddBlacklistDialog = ({
  isOpen,
  editingEntry,
  saveBlacklist,
  closeDialog,
  isLoading = false,
}: Props) => {
  const { t } = useTranslation();
  const isLoadingSlice = useIsLoading(["talents"]);
  const isEditing = !!editingEntry;
  const { localized } = useAppSelector((state) => state);

  const methods = useForm<BlacklistFormValues>({
    defaultValues: defaultBlacklistFormValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isDirty, isValid, isSubmitted },
    reset,
  } = methods;

  useEffect(() => {
    if (!isOpen) {
      reset(defaultBlacklistFormValues);
      return;
    }

    reset(
      editingEntry
        ? mapEntryToBlacklistFormValues(editingEntry, localized.workTitle)
        : defaultBlacklistFormValues,
    );
  }, [isOpen, editingEntry, reset, localized.workTitle]);

  const onSubmit = (formData: BlacklistFormValues) => {
    saveBlacklist(formData);
  };

  const onCancel = () => {
    closeDialog();
    reset(defaultBlacklistFormValues);
  };

  useEffect(() => {
    if (isSubmitted) {
      closeDialog();
      reset(defaultBlacklistFormValues);
    }
  }, [isSubmitted, closeDialog, reset]);

  const title = isEditing
    ? t("talentProfile:editBlacklist")
    : t("talentProfile:newBlacklist");

  return (
    <BasicDialog
      open={isOpen}
      title={title}
      onCancel={onCancel}
      onConfirm={handleSubmit(onSubmit)}
      confirmDisabled={!isDirty || !isValid || isLoading || isLoadingSlice}
      PaperProps={{ sx: { minWidth: "700px" } }}
      content={
        <FormProvider {...methods}>
          <AddBlacklistForm isEditing={isEditing} />
        </FormProvider>
      }
    />
  );
};
